import { useState } from 'react';

import { usePurchaseDesk } from '@/api/purchaseDesk';
import { DocumentHeading } from '@/components/DocumentSheet';
import { controlClass } from '@/components/Field';
import { Button, Card, Notice } from '@/components/PageHeading';
import type { SupplierDeskRow } from '@core/api/purchaseDesk';
import { orderSupplierChoices, orderSupplierLine } from '@core/purchasing/desk';
import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';

import { VendorDrawer } from '../vendors/VendorDrawer';

/**
 * A new purchase order's first question — **"Who are you ordering from?"** (owner, 2026-11-01,
 * canvas "Supplier payment and choice", Supplier A; the phone's `purchases/SupplierFirst.tsx` on the
 * same core `orderSupplierChoices`). The suppliers dealt with lately as cards, what is owed beside
 * each, search, and Add a new supplier — the vendor form with Supply ticked, coming back with them
 * chosen. The order's lines come after.
 */
export function OrderSupplierFirst({
  session,
  trail,
  onPick,
  onClose,
}: {
  session: Session;
  trail: string;
  onPick: (vendorId: number) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const desk = usePurchaseDesk(session.branch?.id, true);
  const { recent, rest } = orderSupplierChoices(desk.data?.suppliers ?? [], search, 6);
  const mayAdd = canAct(session, 'Vendor:Create');

  return (
    <>
      <DocumentHeading
        trail={trail}
        current="New"
        title="Who are you ordering from?"
        subtitle="Step 1 of 2 — then the designs and how many of each size."
        onBack={onClose}
        actions={
          <Button icon="icon icon-arrow-left" shortcut="back" onClick={onClose}>
            Back
          </Button>
        }
      />

      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="sr-only" htmlFor="order-supplier-search">
            Find a supplier
          </label>
          <input
            id="order-supplier-search"
            autoFocus
            value={search}
            placeholder="Supplier name or mobile"
            onChange={(event) => setSearch(event.target.value)}
            className={`${controlClass} max-w-96 flex-1`}
          />
          {mayAdd && (
            <Button icon="icon icon-plus" onClick={() => setAdding(true)}>
              Add a new supplier
            </Button>
          )}
        </div>

        {desk.isError && <Notice tone="danger" title="Your suppliers could not be read." />}

        {recent.length > 0 && (
          <Group title={search.trim() ? 'Found' : 'Dealt with lately'}>
            {recent.map((row) => (
              <SupplierCard key={row.vendorId} row={row} onPick={onPick} />
            ))}
          </Group>
        )}

        {rest.length > 0 && (
          <Group title={recent.length > 0 ? 'Every other supplier' : 'Suppliers'}>
            {rest.map((row) => (
              <SupplierCard key={row.vendorId} row={row} onPick={onPick} />
            ))}
          </Group>
        )}

        {desk.isSuccess && recent.length === 0 && rest.length === 0 && (
          <p className="text-default mb-0">
            {search.trim()
              ? `No supplier matches “${search.trim()}”.`
              : 'No one is marked as a supplier yet.'}
          </p>
        )}

        <p className="text-default mt-4 mb-0 text-[length:var(--scale-list-meta)]">
          Only vendors marked “Supply” are listed.
        </p>
      </Card>

      {adding && (
        <VendorDrawer
          editing={{ mode: 'create', capabilities: ['Supplier'] }}
          onClose={() => setAdding(false)}
          onCreated={(vendor) => onPick(vendor.id)}
        />
      )}
    </>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4">
      <p className="text-default mb-2 text-[11px] font-semibold tracking-wide uppercase">{title}</p>
      <ul className="m-0 grid list-none grid-cols-1 gap-2 ps-0 sm:grid-cols-2 xl:grid-cols-3">
        {children}
      </ul>
    </section>
  );
}

function SupplierCard({
  row,
  onPick,
}: {
  row: SupplierDeskRow;
  onPick: (vendorId: number) => void;
}) {
  const initials = row.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');

  return (
    <li className="list-none">
      <button
        type="button"
        onClick={() => onPick(row.vendorId)}
        className="border-border-color hover:border-primary hover:bg-light flex w-full items-center gap-3 rounded-lg border bg-white p-3 text-start"
      >
        <span
          aria-hidden="true"
          className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-bold"
        >
          {initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold text-gray-900">{row.name}</span>
          <span className="text-default block truncate text-[11px]">{orderSupplierLine(row)}</span>
        </span>
        <i className="icon icon-chevron-right text-default" aria-hidden="true" />
      </button>
    </li>
  );
}
